/** Who is signed in, for the whole tree. One `me()` on load; screens read, never re-fetch. */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { dropSession, isSignedIn, me, type Principal } from "./session";

type SessionState = {
  principal: Principal | null;
  status: "loading" | "ready";
  reload: () => Promise<void>;
  signOut: () => void;
};

const Ctx = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [status, setStatus] = useState<"loading" | "ready">(isSignedIn() ? "loading" : "ready");

  const reload = useCallback(async () => {
    if (!isSignedIn()) {
      setPrincipal(null);
      setStatus("ready");
      return;
    }
    setStatus("loading");
    try {
      setPrincipal(await me());
    } catch {
      setPrincipal(null);
    } finally {
      setStatus("ready");
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const signOut = useCallback(() => {
    dropSession();
    setPrincipal(null);
    setStatus("ready");
  }, []);

  const value = useMemo<SessionState>(
    () => ({ principal, status, reload, signOut }),
    [principal, status, reload, signOut],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSession outside SessionProvider");
  return v;
}
